import React from 'react';

const COMPANIONS = [
  { id: 'solo', label: 'Solo', emoji: '\uD83E\uDDD1' },
  { id: 'couple', label: 'Couple', emoji: '\uD83D\uDC91' },
  { id: 'family', label: 'Family', emoji: '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67\u200D\uD83D\uDC66' },
  { id: 'friends', label: 'Friends', emoji: '\uD83E\uDD1D' },
] as const;

const SEASONS = [
  { id: 'summer', label: 'Summer', emoji: '\u2600\uFE0F' },
  { id: 'winter', label: 'Winter', emoji: '\u2744\uFE0F' },
  { id: 'spring-fall', label: 'Spring/Fall', emoji: '\uD83C\uDF3F' },
  { id: 'anytime', label: 'Anytime', emoji: '\uD83C\uDF0D' },
] as const;

interface StepTravelersProps {
  selectedCompanions: string[];
  selectedSeasons: string[];
  onToggleCompanion: (id: string) => void;
  onToggleSeason: (id: string) => void;
  onContinue: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const StepTravelers: React.FC<StepTravelersProps> = ({
  selectedCompanions,
  selectedSeasons,
  onToggleCompanion,
  onToggleSeason,
  onContinue,
  onBack,
  onSkip,
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Tell us about your trips
        </h1>
        <p className="text-gray-500 mb-8">
          This helps us find the perfect stays for you
        </p>

        {/* Companions */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Who usually travels with you?
          </h2>
          <div className="flex flex-wrap gap-3">
            {COMPANIONS.map((item) => {
              const selected = selectedCompanions.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onToggleCompanion(item.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 transition-all duration-200 text-sm font-medium ${
                    selected
                      ? 'border-primary-500 bg-primary-50 text-primary-700 scale-105'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{item.emoji}</span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Seasons */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            When do you usually travel?
          </h2>
          <div className="flex flex-wrap gap-3">
            {SEASONS.map((item) => {
              const selected = selectedSeasons.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onToggleSeason(item.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 transition-all duration-200 text-sm font-medium ${
                    selected
                      ? 'border-primary-500 bg-primary-50 text-primary-700 scale-105'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{item.emoji}</span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={onContinue}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors text-base"
          >
            Continue
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors text-sm"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepTravelers;
